
import Vue from 'vue';
import EuInput from '../Input/Input.vue';
import EuInputAdvanced from '../InputAdvanced/InputAdvanced.vue';
/**
Explanation: 
This InputField contains Input <b>(variant: basic)</b>, InputAdvance <b>(variant: advance)</b> and labelings. 
This links to Input & InputAdvanced, meaning this accepts their original props.
User can manually put styling into the InputField with the classes below, for example changing the label color etc
      <ul style="padding-left: 16px;">
        <li>
          <b>eu-inputfield-section</b>: the whole container
        </li>
        <li>
          <b>eu-inputfield-label-section</b>: upper label container
        </li>
        <li>
          <b>eu-inputfield-label</b>: upper left label
        </li>
        <li>
          <b>eu-inputfield-sublabel</b>: upper right sublabel
        </li>
        <li>
          <b>eu-inputfield-input</b>: the basic input
        </li>
        <li>
          <b>eu-inputfield-advinput</b>: the advance input
        </li>
        <li>
          <b>eu-inputfield-below-section</b>: lower label container (information)
        </li>
        <li>
          <b>eu-inputfield-leftinfo</b>: left info
        </li>
        <li>
          <b>eu-inputfield-rightinfo</b>: right info
        </li>
      </ul>
**/
export default Vue.extend<any, any, any, any>({
  name: 'InputField',
  data() {
    return {
      focus: false,
      open: false,
      selectedItem: {}
    };
  },
  components: {
    EuInput,
    EuInputAdvanced
  },
  props: {
    /**
     * Value of input
     */
    value: {
      type: [String, Number, Object],
      required: true
    },
    /**
     * Variant of input field; basic or advance
     */
    variant: {
      type: String,
      default: 'basic'
    },
    /**
     * Set label on top of input
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Set label on top of input
     */
    sublabel: {
      type: String,
      default: ''
    },
    /**
     * Set label on right side below input
     */
    rightinfo: {
      type: String,
      default: ''
    },
    /**
     * Set label on left side below input
     */
    leftinfo: {
      type: String,
      default: ''
    },
    /**
     * Set maximum length for input value (for all types)
     */
    maxLength: {
      type: Number,
      default: Number.POSITIVE_INFINITY
    },
    /**
     * Set minimum length for input value (for all types)
     */
    minLength: {
      type: Number,
      default: 0
    },
    /**
     * Set exact length for input value (for all types)
     */
    exactLength: {
      type: Number
    },
    /**
     * The ID, need to string or number
     */
    id: {
      type: [Number, String],
      required: false
    },
    /**
     * Set placeholder in the input
     */
    placeholder: {
      type: String,
      default: 'Placeholder here'
    },
    /**
     * Set input on error state
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Set input on success state
     */
    success: {
      type: Boolean,
      default: false
    },
    /**
     * Put label below the input
     */
    bottomLabel: {
      type: String,
      default: ''
    },
    /**
     * Change input formatter type (example: currency)
     */
    format: {
      type: String,
      default: ''
    },
    /**
     * Set the separator for currency (example: , or .)
     */
    separator: {
      type: String,
      default: ','
    },
    /**
     * To show the left label, set the label text
     */
    fieldLabel: {
      type: String,
      default: ''
    },
    /**
     * Set image on the left label
     */
    imageField: {
      type: String,
      default: ''
    },
    /**
     * To show arrow
     */
    selectionLabel: {
      type: Boolean,
      default: false
    },
    /**
     * Set arrow color
     */
    arrowColor: {
      type: String,
      default: '#f87300'
    },
    /**
     * To show the check function, use this to set the check label
     */
    checkLabel: {
      type: String,
      default: ''
    },
    /**
     * To customize own section on left or right side inside input
     */
    customSection: {
      type: String,
      default: ''
    },
    /**
     * To apply dropdown selection on left side of input
     */
    selectionOptions: {
      type: Array,
      default: null
    },
    /**
     * Set button type (primary, secondary, default, destructive)
     */
    buttonType: {
      type: String,
      default: 'primary'
    },
    /**
     * Set label color when input is on focus
     */
    focusColor: {
      type: String,
      default: ''
    },
    /**
     * Clickonly dropdown
     */
    clickonly: {
      type: Boolean,
      default: false
    },
    /**
     * Close label on tray (for phone number input)
     */
    modalTitle: {
      type: String,
      default: 'Modal Title'
    },
    /**
     * Pass custom regex
     */
    customRegex: {
      type: String,
      default: ''
    },
    /**
     * Set input to required
     */
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Make the input prevent to paste
     */
    pasteInput: {
      type: Boolean,
      default: true
    },
    /**
     * Make the input text auto capitalize
     */
    capitalize: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    handleInput(e: Event) {
      /**
       * Input value
       * @event input
       * @type {function}
       */
      this.$emit('input', e);
    },
    handleChange(e: Event) {
      /**
       * On change
       * @event change
       * @type {function}
       */
      this.$emit('change', e);
    },
    handleFocus(e: Event) {
      this.focus = true;
      /**
       * On focus
       * @event focus
       * @type {function}
       */
      this.$emit('focus', e);
    },
    handleBlur(e: Event) {
      this.focus = false;
      /**
       * On blur
       * @event blur
       * @type {function}
       */
      this.$emit('blur', e);
    },
    handleKeyPress(e: Event) {
      /**
       * When on keypress
       * @event keypress
       * @type {function}
       */
      this.$emit('keypress', e);
    },
    handleChangeState(e: Event) {
      /**
       * Returns validation when on keypress.
       * This method only works if props required is given. Meaning if the value is null, changeState = false.
       * @event changeState
       * @type {function}
       */
      this.$emit('changeState', e);
    },
    handleState(e: Event) {
      /**
       * Returns validation when on blur.
       * This method only works if props required is given. Meaning if the value is null, inputState = false.
       * @event inputState
       * @type {function}
       */
      this.$emit('inputState', e);
    },
    handleClick(e: Event) {
      /**
       * Method to click
       * @event click
       * @type {function}
       */
      this.$emit('click', e);
    }
  }
});
